import { Bell, Play, ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import podcastUpcoming from "@/assets/podcast-upcoming.jpg";

interface Announcement {
  id: string;
  type: "video" | "article" | "update" | "upcoming";
  title: string;
  description: string;
  link: string;
  date: string;
  isNew?: boolean;
  isUpcoming?: boolean;
  image?: string;
}

const announcements: Announcement[] = [
  {
    id: "0",
    type: "upcoming",
    title: "النمو دون حوكمة: لماذا قد يتحول التوسع في مشاريع التشييد إلى خسائر",
    description: "بودكاست مباشر مع المضيف محمد شكري والضيوف رامي سالم ومحمد طلعت",
    link: "/podcast",
    date: "January 13, 2025 - 8:00am",
    isUpcoming: true,
    image: podcastUpcoming
  },
  {
    id: "1",
    type: "video",
    title: "كيف تخطط مشروعك في دقائق باستخدام الذكاء الاصطناعي",
    description: "تعرف على كيفية استخدام أدوات الذكاء الاصطناعي لتخطيط مشاريعك بشكل أسرع وأكثر كفاءة.",
    link: "https://youtu.be/xm1Vp9pVhZw",
    date: "New",
    isNew: true
  },
  {
    id: "2",
    type: "video",
    title: "أسرع طريق للتحول الرقمي في الإنشاءات باستخدام Power Apps",
    description: "تعرف على كيفية استخدام Power Apps لتسريع التحول الرقمي في قطاع الإنشاءات وتحسين كفاءة إدارة المشاريع.",
    link: "https://youtu.be/klR7XY51Nsg",
    date: "New",
    isNew: false
  }
];

const AnnouncementsSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 lg:py-20 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
              <Bell className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <h2 className="font-playfair text-2xl lg:text-3xl font-bold text-foreground">
                Latest Updates
              </h2>
              <p className="text-muted-foreground text-sm">
                Stay informed with our newest content
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="gap-2 text-destructive hover:text-destructive"
            onClick={() => navigate('/podcast')}
          >
            View All
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid gap-4">
          {announcements.map((announcement) => (
            <div
              key={announcement.id}
              className={`bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all hover:border-destructive/30 cursor-pointer group ${announcement.isUpcoming ? 'flex flex-col md:flex-row' : ''}`}
              onClick={() => announcement.isUpcoming ? navigate(announcement.link) : window.open(announcement.link, '_blank')}
            >
              {announcement.isUpcoming && announcement.image && (
                <div className="md:w-64 h-48 md:h-auto flex-shrink-0">
                  <img 
                    src={announcement.image} 
                    alt={announcement.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex items-start gap-4 p-5 flex-1">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${announcement.isUpcoming ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-destructive/10 group-hover:bg-destructive/20'}`}>
                  {announcement.isUpcoming ? (
                    <Calendar className="h-6 w-6 text-primary" />
                  ) : (
                    <Play className="h-6 w-6 text-destructive" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {announcement.isNew && (
                      <Badge className="bg-destructive text-destructive-foreground text-xs">
                        New
                      </Badge>
                    )}
                    {announcement.isUpcoming && (
                      <Badge className="bg-primary text-primary-foreground text-xs">
                        Upcoming
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">
                      {announcement.type}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground mb-1 group-hover:text-destructive transition-colors line-clamp-1">
                    {announcement.title}
                  </h3>
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-2">
                    {announcement.description}
                  </p>
                  {announcement.isUpcoming && (
                    <p className="text-sm font-medium text-primary">
                      {announcement.date}
                    </p>
                  )}
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-destructive transition-colors flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AnnouncementsSection;
